import { AxiosResponse } from "axios";
import { Nullable } from "../../../Types/Common";
import ApiService from "../../ApiService";

enum ReportZoneEndpoints {
  EMPLOYEE_REIMBURSEMENT = "/report/employee-reimbursement",
  EMPLOYEE_CONVEYANCE = "/report/employee-conveyance",
  EMPLOYEE_ATTENDANCE = "/report/employee-attendance",
  EMPLOYEE_ATTENDANCE_COMPOFF_SUMMARY = "/report/employee-attendance-compoff-summary",
  EMPLOYEE_SALARY = "/report/employee-salary-report",
  EMPLOYEE_LEAVE = "/report/employee-leave",
  EMPLOYEE_MONTHLY_WISE = "/report/employee-month-wise",
}

export interface IReimbursementList {
  amount: Nullable<number>;
  bill: Nullable<string>;
  date: Nullable<string>;
  department_name: Nullable<string>;
  description: Nullable<string>;
  destination: Nullable<string>;
  email: Nullable<string>;
  employee_code: Nullable<string>;
  member_names: Nullable<any>;
  members: Nullable<any>;
  name: Nullable<string>;
  purpose: Nullable<string>;
  status: Nullable<string>;
  submitted_on: Nullable<string>;
}

export interface IReimbursementReportDetails {
  current_page: number;
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: Nullable<string>;
  next_page_url: Nullable<string>;
  path: Nullable<string>;
  per_page: number;
  prev_page_url: Nullable<string>;
  to: number;
  total: number;
  data: IReimbursementList[];
}

export interface IReimbursementReportListResponse {
  success: boolean;
  start_date: string;
  end_date: string;
  data: IReimbursementReportDetails | IReimbursementList[];
}

export interface IConveyanceReport {
  current_page: Nullable<number>;
  first_page_url: Nullable<string>;
  from: Nullable<number>;
  last_page: Nullable<number>;
  last_page_url: Nullable<string>;
  next_page_url: Nullable<string>;
  path: Nullable<string>;
  per_page: Nullable<number>;
  prev_page_url: Nullable<string>;
  to: Nullable<number>;
  total: number;
  data: IConveyanceReportList[];
}

export interface IConveyanceReportList {
  amount: Nullable<number>;
  conveyance_code: Nullable<string>;
  conveyance_date: Nullable<string>;
  department: Nullable<string>;
  distance: Nullable<string>;
  job_type: Nullable<string>;
  email: Nullable<string>;
  employee_code: Nullable<string>;
  image: Nullable<string>;
  mode: Nullable<string>;
  reason: Nullable<string>;
  status: Nullable<string>;
  submitted_on: Nullable<string>;
  total_conveyance: Nullable<string>;
  travel_to: Nullable<string>;
  travelfrom: Nullable<string>;
  user_id: Nullable<number>;
  username: Nullable<string>;
}

export interface IConveyanceReportListResponse {
  message: string;
  status: string;
  data: IConveyanceReport;
}

export interface IEmployeeSalarySummary {
  absent_days: Nullable<number>;
  department: Nullable<string>;
  email: Nullable<string>;
  emp_code: Nullable<string>;
  emp_name: Nullable<string>;
  doj: Nullable<string>;
  present_days: Nullable<number>;
  total_days: Nullable<number>;
  user_id: Nullable<number>;
  job_type: Nullable<string>;
  leaves: { [key: string]: Nullable<number> };
  leave_dates: Array<string>;
  holidays_count: number;
  sundays_count: number;
  pending_attendance_corrections: number;
  last_year_carry_forwarded: number;
  leave_balance:number;
  leave_taken:number;
  monthly_leave_occured:number;
  manager_email:string;
}
export interface IEmployeeSalaryList {
  end_date: string;
  start_date: string;
  sundays: Nullable<number>;
  total_days: Nullable<number>;
  leave_types: Array<string>;
  summary: IEmployeeSalarySummary[];
}
export interface IEmployeeSalaryListResponse {
  message: string;
  success: boolean;
  data: IEmployeeSalaryList;
}

export interface IEmployeeLeaveList {
  against_compoff_date: Nullable<string>;
  approved_by: Nullable<Array<string>>;
  approvers: Nullable<string>;
  days: Nullable<string>;
  department_name: Nullable<string>;
  email: Nullable<string>;
  employee_code: Nullable<string>;
  employee_id: Nullable<number>;
  job_type: Nullable<string>;
  employee_name: Nullable<string>;
  leave_end_date: Nullable<string>;
  leave_start_date: Nullable<string>;
  leave_status: Nullable<string>;
  leave_type: Nullable<string>;
  reason: Nullable<string>;
}

export interface IEmployeeLeaveListReponse {
  status: boolean;
  leaves: Array<IEmployeeLeaveList>;
}

export interface IEmployeeAttendanceCompoffAttendance {
  date: Nullable<string>;
  day: Nullable<string>;
  in_time: Nullable<string>;
  out_time: Nullable<string>;
  working_hours: Nullable<string>;
  remark: Nullable<string>;
}

export interface IEmployeeAttendanceCompoffStatus {
  date: Nullable<string>;
  day_type: Nullable<string>;
  hours_worked: Nullable<number>;
  credited: Nullable<boolean>;
  eligible: Nullable<boolean>;
  reason: Nullable<string>;
  final_remark: Nullable<string>;
}

export interface IEmployeeAttendanceCompoffSummaryList {
  employee_code: Nullable<string>;
  employee_name: Nullable<string>;
  email: Nullable<string>;
  department: Nullable<string>;
  job_type: Nullable<string>;
  total_working_hours: Nullable<string>;
  attendance: Array<IEmployeeAttendanceCompoffAttendance>;
  comp_off_status: Array<IEmployeeAttendanceCompoffStatus>;
}

export interface IEmployeeAttendanceCompoffSummaryPagination {
  current_page: number;
  first_page_url: Nullable<string>;
  from: Nullable<number>;
  last_page: number;
  last_page_url: Nullable<string>;
  next_page_url: Nullable<string>;
  path: Nullable<string>;
  per_page: number | string;
  prev_page_url: Nullable<string>;
  to: Nullable<number>;
  total: number;
  data: Array<IEmployeeAttendanceCompoffSummaryList>;
}

export interface IEmployeeAttendanceCompoffSummaryResponse {
  success: boolean;
  start_date: string;
  end_date: string;
  data: IEmployeeAttendanceCompoffSummaryPagination;
}

export interface IEmployeesMonthlyDetails {
  id: number;
  name: Nullable<string>;
  email: Nullable<string>;
  doj: Nullable<string>;
  job_type: Nullable<string>;
  exit_date: Nullable<string>;
  status: Nullable<string>;
  emp_code: Nullable<string>;
  department_name: Nullable<string>;
}

export interface IEmployeeMonthlyWiseListResponse {
  success: boolean;
  data: {
    [key: string]: {
      details: {
        active: Array<IEmployeesMonthlyDetails>;
        exited: Array<IEmployeesMonthlyDetails>;
        new_joinees: Array<IEmployeesMonthlyDetails>;
        combined: Array<IEmployeesMonthlyDetails>;
      };
      active_count: number;
      new_joinees_count: number;
      exited_count: number;
      combined_count: number;
    };
  };
}

export type IBulkEmployeeAttendanceReportResponse = AxiosResponse<Blob>;

class ReportService extends ApiService {
  async reimbursmentReport({
    page,
    size,
    startDate,
    endDate,
    reimbursementType,
    employeeId,
  }: {
    page?: number;
    size?: number;
    startDate?: string;
    endDate?: string;
    reimbursementType?: string;
    employeeId?: string;
  }) {
    return this.post<IReimbursementReportListResponse>(
      ReportZoneEndpoints.EMPLOYEE_REIMBURSEMENT,
      {
        page,
        size,
        start_date: startDate,
        end_date: endDate,
        reimbursement_type: reimbursementType,
        employee_id: employeeId,
      }
    );
  }

  async conveyanceReport({
    startDate,
    endDate,
    employeeId,
    page,
    size,
    jobType,
  }: {
    startDate?: string;
    endDate?: string;
    employeeId?: string;
    page?: number;
    size?: number;
    jobType?: string;
  }): Promise<IConveyanceReportListResponse> {
    return this.post<IConveyanceReportListResponse>(
      ReportZoneEndpoints.EMPLOYEE_CONVEYANCE,
      {
        start_date: startDate,
        end_date: endDate,
        employee_id: employeeId,
        page,
        size,
        job_type: jobType,
      }
    );
  }

  async bulkEmployeeAttendanceReport({
    startDate,
    endDate,
    employeeId,
    jobType,
  }: {
    startDate?: string;
    endDate?: string;
    employeeId?: Array<number>;
    jobType?: string;
  }): Promise<IBulkEmployeeAttendanceReportResponse> {
    return this.post<IBulkEmployeeAttendanceReportResponse>(
      ReportZoneEndpoints.EMPLOYEE_ATTENDANCE,
      {
        start_date: startDate,
        end_date: endDate,
        employee_id: employeeId,
        job_type: jobType,
      },
      {
        responseType: "blob",
      }
    );
  }

  async employeeSalaryReport({
    startDate,
    endDate,
    employeeId,
    jobType,
  }: {
    startDate?: string;
    endDate?: string;
    employeeId?: string;
    jobType?: string;
  }) {
    return this.post<IEmployeeSalaryListResponse>(
      ReportZoneEndpoints.EMPLOYEE_SALARY,
      {
        start_date: startDate,
        end_date: endDate,
        employee_id: employeeId,
        job_type: jobType,
      }
    );
  }

  async employeeLeaveReport({
    startDate,
    endDate,
    employeeId,
    jobType,
  }: {
    startDate: string;
    endDate: string;
    employeeId?: Array<number>;
    jobType?: string;
  }) {
    return this.post<IEmployeeLeaveListReponse>(
      ReportZoneEndpoints.EMPLOYEE_LEAVE,
      {
        start_date: startDate,
        end_date: endDate,
        employee_id: employeeId,
        job_type: jobType,
      }
    );
  }

  async employeeAttendanceCompoffSummary({
    startDate,
    endDate,
    employeeId,
    jobType,
    page = 1,
    size = 10,
  }: {
    startDate: string;
    endDate: string;
    employeeId?: Array<number>;
    jobType?: string;
    page?: number;
    size?: number;
  }) {
    return this.post<IEmployeeAttendanceCompoffSummaryResponse>(
      `${ReportZoneEndpoints.EMPLOYEE_ATTENDANCE_COMPOFF_SUMMARY}?page=${page}&size=${size}`,
      {
        start_date: startDate,
        end_date: endDate,
        employee_id: employeeId,
        job_type: jobType,
      }
    );
  }

  async employeeMonthlyWiseReport({
    startDate,
    endDate,
    jobType,
  }: {
    startDate?: string;
    endDate?: string;
    jobType?: string;
  }) {
    return this.post<IEmployeeMonthlyWiseListResponse>(
      ReportZoneEndpoints.EMPLOYEE_MONTHLY_WISE,
      {
        start_date: startDate,
        end_date: endDate,
        job_type: jobType,
      }
    );
  }
}
export const reportService = new ReportService();
